import type { BellItem, FollowTarget } from "@nexxt/app-kit";
import type { LinkOptions } from "@tanstack/react-router";

// The page of Backstage of an item of the bell (ADR-280): the one its email links to, as `gatePath` of notifications
// does for a gate; what waits for an approval for one guarding what has no page here (ADR-308; bell-links.test.ts
// holds the two together). Every type the bell or a follow names has its page (ADR-281, ADR-308).

export function linkOf({
  templateCode,
  entityType,
  entityId,
}: Pick<BellItem, "templateCode" | "entityType" | "entityId">): LinkOptions | null {
  if (entityId === null) return null;
  // ADR-199: the shipment beyond the release waits among the shipments of the warehouse, though its row is the order's
  if (templateCode === "shipment.over_released")
    return { to: "/warehouse-shipments", search: { reason: "over_released" } };
  switch (entityType) {
    case "catalog.linesheet":
      return { to: "/linesheets/$linesheetId", params: { linesheetId: entityId } };
    case "catalog.attribute_task":
      return { to: "/attributes/$taskId", params: { taskId: entityId } };
    case "procurement.purchase_order":
      return { to: "/purchase-orders/$purchaseOrderId", params: { purchaseOrderId: entityId } };
    case "procurement.inbound":
      return { to: "/inbounds/$inboundKey", params: { inboundKey: entityId } };
    case "sales.sales_order":
      return { to: "/sales-orders/$salesOrderId", params: { salesOrderId: entityId } };
    case "claims.claim":
      return { to: "/claims", search: { panel: entityId } };
    // of the movements only a vendor return is followed (ADR-309); a gate on one is the transfer's, or the refusal of a
    // vendor return, which the route of the movement sends on to the vendor return
    case "logistics.stock_movement":
      return templateCode.startsWith("vendor_return.") || templateCode.startsWith("follow.")
        ? { to: "/vendor-returns/$vendorReturnId", params: { vendorReturnId: entityId } }
        : { to: "/stock-movements/$movementId", params: { movementId: entityId } };
    case "logistics.fulfillment":
      return { to: "/fulfillments/$fulfillmentId", params: { fulfillmentId: entityId } };
    // the rows asking for an action of ADR-309, at the page their email opens
    case "documents.document":
      return { to: "/documents", search: { panel: entityId } };
    case "integration.erp_file":
      return { to: "/erp-files" };
    case "reference.location":
      return { to: "/stock-availability", search: { location: entityId, sort: "-imsUpdatedAt" } };
    case "party.mfa_reset_request":
      return { to: "/admin/users" };
    default:
      return templateCode.startsWith("gate.") ? { to: "/admin/approvals" } : null;
  }
}

/** The page of an object the user follows (ADR-281): the one its bell rows lead to. */
export const followedLink = ({ entityType, entityId }: FollowTarget): LinkOptions | null =>
  linkOf({ templateCode: "follow.status_changed", entityType, entityId });
